import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Plus, Sparkles, Trash2, Undo2 } from "lucide-react";
import { AdminPageHeader, AdminSectionCard } from "@/components/shell/AdminPage";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useHermes } from "@/components/hermes/HermesProvider";
import { useAccountWorkspace } from "@/lib/account-workspace";
import {
  enabledChannelIds,
  fetchAccountChannels,
  type AccountChannel,
} from "@/lib/account-channels";
import {
  HERMES_WRITE_ACCEPTED_EVENT,
  type HermesWriteAcceptedDetail,
} from "@/lib/hermes-knowledge-entry";
import {
  JourneyApiError,
  createJourney,
  deleteJourney,
  disableJourney,
  enableJourney,
  getJourney,
  updateJourney,
  DEFAULT_SEND_WINDOW_END,
  DEFAULT_SEND_WINDOW_START,
  normalizeHhmm,
  validateSendWindow,
  type JourneyChannelPreference,
  type JourneyInputMode,
  type JourneyNodeInput,
} from "@/lib/journey-api";
import type { JourneyOutletContext } from "@/pages/journey/JourneyLayout";
import { cn } from "@/lib/utils";

type DraftNode = JourneyNodeInput & { key: string; _deleted?: boolean };

function newNode(offsetDays = 0): DraftNode {
  return {
    key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    input_mode: "form",
    offset_days: offsetDays,
    trigger: "",
    channel_preference: "wecom",
    instruction: "",
    prompt: "",
    send_window_start: DEFAULT_SEND_WINDOW_START,
    send_window_end: DEFAULT_SEND_WINDOW_END,
  };
}

function mapApiNode(node: {
  id?: number;
  input_mode?: string;
  offset_days: number;
  trigger?: string | null;
  channel_preference?: string | null;
  instruction?: string;
  prompt?: string | null;
  send_window_start?: string;
  send_window_end?: string;
}): DraftNode {
  return {
    key: String(node.id ?? `${node.offset_days}-${node.instruction ?? ""}`),
    input_mode: (node.input_mode === "freeform" ? "freeform" : "form") as JourneyInputMode,
    offset_days: node.offset_days,
    trigger: node.trigger ?? "",
    channel_preference: (node.channel_preference as JourneyChannelPreference) || "wecom",
    instruction: node.instruction ?? "",
    prompt: node.prompt ?? "",
    send_window_start: normalizeHhmm(node.send_window_start || DEFAULT_SEND_WINDOW_START),
    send_window_end: normalizeHhmm(node.send_window_end || DEFAULT_SEND_WINDOW_END),
  };
}

function activeNodes(nodes: DraftNode[]) {
  return nodes.filter((node) => !node._deleted);
}

/** 新建 / 编辑旅程节点；双模式 + 账户渠道偏好；启用受企微就绪门禁 */
export default function JourneyEditPage() {
  const { brandId, journeyId } = useParams();
  const isNew = !journeyId || journeyId === "new";
  const navigate = useNavigate();
  const { accountId } = useAccountWorkspace();
  const { ready, refreshReady } = useOutletContext<JourneyOutletContext>();
  const { startJourneyAuthoringSession } = useHermes();

  const [name, setName] = useState("");
  const [nodes, setNodes] = useState<DraftNode[]>([newNode(0)]);
  const [status, setStatus] = useState<string>("draft");
  const [channels, setChannels] = useState<AccountChannel[]>([]);
  const [isLoading, setIsLoading] = useState(!isNew);
  const [isSaving, setIsSaving] = useState(false);
  const [isEnabling, setIsEnabling] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const base = `/accounts/${accountId}/brands/${brandId}/apps/journey`;
  const selectableChannels = channels.filter((c) => c.enabled);

  const loadChannels = useCallback(async () => {
    try {
      const res = await fetchAccountChannels(accountId);
      setChannels(res.channels ?? []);
    } catch {
      setChannels([{ id: "wecom", label: "企微接入", enabled: ready, status: ready ? "ready" : "not_ready", coming_soon: false }]);
    }
  }, [accountId, ready]);

  const load = useCallback(async () => {
    if (isNew || !journeyId) return;
    setIsLoading(true);
    setError(null);
    try {
      const journey = await getJourney(accountId, journeyId);
      setName(journey.name);
      setStatus(journey.status);
      setNodes(journey.nodes.length > 0 ? journey.nodes.map(mapApiNode) : [newNode()]);
    } catch (caught) {
      setError(caught instanceof JourneyApiError ? caught.message : "无法加载旅程");
    } finally {
      setIsLoading(false);
    }
  }, [accountId, isNew, journeyId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadChannels();
  }, [loadChannels]);

  useEffect(() => {
    function onWriteAccepted(event: Event) {
      const detail = (event as CustomEvent<HermesWriteAcceptedDetail>).detail;
      if (!detail || detail.action !== "journey_upsert") return;
      if (!brandId) return;
      const eventBrandId = detail.brand_id ?? Number(detail.payload?.brand_id);
      if (Number(eventBrandId) !== Number(brandId)) return;

      const eventJourneyId = detail.journey_id ?? Number(detail.payload?.journey_id);
      if (!Number.isFinite(eventJourneyId) || eventJourneyId <= 0) return;

      if (isNew) {
        navigate(`${base}/journeys/${eventJourneyId}`, { replace: true });
        toast.success("已按 AI 确认结果创建旅程");
        return;
      }
      if (String(eventJourneyId) === String(journeyId)) {
        void load();
        toast.success("已按确认结果刷新手册（未保存的本地修改已覆盖）");
      }
    }
    window.addEventListener(HERMES_WRITE_ACCEPTED_EVENT, onWriteAccepted);
    return () => window.removeEventListener(HERMES_WRITE_ACCEPTED_EVENT, onWriteAccepted);
  }, [base, brandId, isNew, journeyId, load, navigate]);

  function handleAiAssist() {
    if (!brandId) return;
    startJourneyAuthoringSession({
      mode: "journey_authoring",
      accountId,
      brandId: Number(brandId),
      journeyId: isNew ? null : Number(journeyId),
    });
  }

  function updateNode(key: string, patch: Partial<DraftNode>) {
    setNodes((current) => current.map((node) => (node.key === key ? { ...node, ...patch } : node)));
  }

  function setNodeMode(key: string, mode: JourneyInputMode) {
    updateNode(key, { input_mode: mode });
  }

  function softDeleteNode(key: string) {
    setNodes((current) => {
      const active = activeNodes(current);
      if (active.length <= 1) return current;
      return current.map((node) => (node.key === key ? { ...node, _deleted: true } : node));
    });
  }

  function undoDeleteNode(key: string) {
    updateNode(key, { _deleted: false });
  }

  function insertBelow(index: number) {
    setNodes((current) => {
      const next = [...current];
      next.splice(index + 1, 0, newNode(0));
      return next;
    });
  }

  function moveNode(index: number, delta: number) {
    setNodes((current) => {
      const target = index + delta;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      const tmp = next[index]!;
      next[index] = next[target]!;
      next[target] = tmp;
      return next;
    });
  }

  async function handleSave(event: FormEvent) {
    event.preventDefault();
    if (!brandId) return;

    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error("请填写旅程名称");
      return;
    }

    const kept = activeNodes(nodes);
    if (kept.length === 0) {
      toast.error("至少保留一个节点");
      return;
    }

    const enabledIds = enabledChannelIds(channels);

    for (const node of kept) {
      if (node.input_mode === "form") {
        const windowError = validateSendWindow(node.send_window_start, node.send_window_end);
        if (windowError) {
          toast.error(`节点「偏移 ${node.offset_days} 天」：${windowError}`);
          return;
        }
        if (!node.instruction.trim()) {
          toast.error("表单模式节点须填写执行说明");
          return;
        }
        const pref = node.channel_preference || "wecom";
        if (!enabledIds.includes(pref)) {
          toast.error("渠道偏好须选择账户已开启的渠道（可前往账户「渠道」页）");
          return;
        }
      } else if (!node.prompt?.trim()) {
        toast.error("自由提示词模式须填写节点提示词");
        return;
      }
    }

    const payloadNodes: JourneyNodeInput[] = kept.map((node) => ({
      input_mode: node.input_mode,
      offset_days: Number(node.offset_days) || 0,
      trigger: node.trigger?.trim() || null,
      channel_preference: (node.channel_preference || "wecom") as JourneyChannelPreference,
      instruction: node.instruction.trim(),
      prompt: node.prompt?.trim() || "",
      send_window_start: normalizeHhmm(node.send_window_start),
      send_window_end: normalizeHhmm(node.send_window_end),
    }));

    setIsSaving(true);
    try {
      if (isNew) {
        const created = await createJourney(accountId, {
          brand_id: brandId,
          name: trimmedName,
          nodes: payloadNodes,
        });
        toast.success("草稿已保存");
        navigate(`${base}/journeys/${created.id}`, { replace: true });
      } else if (journeyId) {
        const updated = await updateJourney(accountId, journeyId, {
          name: trimmedName,
          nodes: payloadNodes,
        });
        setStatus(updated.status);
        setNodes(updated.nodes.length > 0 ? updated.nodes.map(mapApiNode) : [newNode()]);
        toast.success("已保存");
      }
    } catch (caught) {
      toast.error(caught instanceof JourneyApiError ? caught.message : "保存失败");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleEnable() {
    if (!journeyId || isNew) return;
    if (!ready) {
      toast.error("请先完成企微接入后才能启用旅程");
      return;
    }

    setIsEnabling(true);
    try {
      await refreshReady();
      const updated = await enableJourney(accountId, journeyId);
      setStatus(updated.status);
      toast.success("旅程已启用");
    } catch (caught) {
      toast.error(caught instanceof JourneyApiError ? caught.message : "启用失败");
    } finally {
      setIsEnabling(false);
    }
  }

  async function handleDisable() {
    if (!journeyId || isNew) return;

    setIsDisabling(true);
    try {
      const updated = await disableJourney(accountId, journeyId);
      setStatus(updated.status);
      toast.success("旅程已关闭");
    } catch (caught) {
      toast.error(caught instanceof JourneyApiError ? caught.message : "关闭失败");
    } finally {
      setIsDisabling(false);
    }
  }

  async function handleDelete() {
    if (!journeyId || isNew) return;
    if (status === "enabled") {
      toast.error("请先关闭旅程后再删除");
      setDeleteOpen(false);
      return;
    }

    setIsDeleting(true);
    try {
      await deleteJourney(accountId, journeyId);
      toast.success("旅程已删除");
      navigate(`${base}/settings/journeys`, { replace: true });
    } catch (caught) {
      toast.error(caught instanceof JourneyApiError ? caught.message : "删除失败");
    } finally {
      setIsDeleting(false);
      setDeleteOpen(false);
    }
  }

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">正在加载旅程…</p>;
  }

  if (error) {
    return (
      <div className="space-y-3">
        <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">
          {error}
        </p>
        <Link to={`${base}/settings/journeys`} className="text-sm text-primary-readable hover:underline">
          返回旅程配置
        </Link>
      </div>
    );
  }

  const isEnabled = status === "enabled";
  const canEnable = !isNew && !isEnabled;
  const canDisable = !isNew && isEnabled;
  const canDelete = !isNew;
  const statusBusy = isSaving || isEnabling || isDisabling || isDeleting;
  const activeNodeCount = activeNodes(nodes).length;

  return (
    <>
      <form className="space-y-6" onSubmit={(event) => void handleSave(event)}>
        <AdminPageHeader
          title={isNew ? "新建旅程" : "编辑旅程"}
          description="用表单或自由提示词写手册步骤；到点后由 AI 决定是否触达，需在企微确认后发出。"
          actions={
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" onClick={handleAiAssist}>
                <Sparkles data-icon="inline-start" />
                AI 协助
              </Button>
              {canDelete ? (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  disabled={statusBusy || isEnabled}
                  title={isEnabled ? "请先关闭旅程后再删除" : "删除整趟旅程"}
                  onClick={() => setDeleteOpen(true)}
                >
                  删除
                </Button>
              ) : null}
              {canEnable ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={!ready || statusBusy}
                  title={!ready ? "请先完成企微接入后才能启用旅程" : undefined}
                  onClick={() => void handleEnable()}
                >
                  {isEnabling ? "启用中…" : "启用旅程"}
                </Button>
              ) : null}
              {canDisable ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={statusBusy}
                  onClick={() => void handleDisable()}
                >
                  {isDisabling ? "关闭中…" : "关闭旅程"}
                </Button>
              ) : null}
              <Button type="submit" size="sm" disabled={statusBusy}>
                {isSaving ? "保存中…" : "保存草稿"}
              </Button>
            </div>
          }
        />

        {!ready && canEnable ? (
          <p className="text-sm text-muted-foreground">
            启用按钮已禁用。
            <Link to={`/accounts/${accountId}/channels`} className="ml-1 text-primary-readable hover:underline">
              去完成企微接入
            </Link>
          </p>
        ) : null}

        {isEnabled ? (
          <p className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-800 dark:text-emerald-300">
            此旅程已启用。需要停用时点「关闭旅程」；关闭后才能删除。
          </p>
        ) : null}

        <AdminSectionCard title="基本信息">
          <div className="max-w-md space-y-4">
            <div className="space-y-2">
              <Label htmlFor="journey-name">旅程名称</Label>
              <Input
                id="journey-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="例如：新客户欢迎旅程"
                required
              />
            </div>
          </div>
        </AdminSectionCard>

        <div className="space-y-4">
          <div>
            <h2 className="text-sm font-medium">节点</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              每节点可选「表单」或「自由提示词」；切换保留另一侧草稿。时区：Asia/Shanghai。
            </p>
          </div>
          {nodes.map((node, index) => {
            const deleted = Boolean(node._deleted);
            const canSoftDelete = activeNodeCount > 1 && !deleted;
            const prefEnabled =
              !node.channel_preference ||
              selectableChannels.some((c) => c.id === node.channel_preference);
            return (
              <div key={node.key} className="space-y-2">
                <div
                  className={`space-y-3 rounded-lg border border-border bg-card p-4 transition-opacity ${
                    deleted ? "opacity-50" : ""
                  }`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-medium">
                      节点 {index + 1}
                      {deleted ? (
                        <span className="ml-2 text-xs font-normal text-muted-foreground">（已标记删除）</span>
                      ) : null}
                    </span>
                    <div className="flex flex-wrap items-center gap-1">
                      <Button type="button" variant="ghost" size="sm" disabled={index === 0} title="上移" onClick={() => moveNode(index, -1)}>
                        <ArrowUp data-icon="inline-start" />
                        上移
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={index >= nodes.length - 1}
                        title="下移"
                        onClick={() => moveNode(index, 1)}
                      >
                        <ArrowDown data-icon="inline-start" />
                        下移
                      </Button>
                      {deleted ? (
                        <Button type="button" variant="ghost" size="sm" onClick={() => undoDeleteNode(node.key)}>
                          <Undo2 data-icon="inline-start" />
                          撤销删除
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={!canSoftDelete}
                          title={!canSoftDelete ? "至少保留一个节点" : "标记删除，保存后才真正去掉"}
                          onClick={() => softDeleteNode(node.key)}
                        >
                          <Trash2 data-icon="inline-start" />
                          删除
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-1 rounded-md border border-border p-1" role="tablist" aria-label="输入模式">
                    {(
                      [
                        ["form", "表单"],
                        ["freeform", "自由提示词"],
                      ] as const
                    ).map(([mode, label]) => (
                      <button
                        key={mode}
                        type="button"
                        role="tab"
                        aria-selected={node.input_mode === mode}
                        disabled={deleted}
                        className={cn(
                          "flex-1 rounded px-3 py-1.5 text-sm transition-colors",
                          node.input_mode === mode
                            ? "bg-primary text-primary-foreground"
                            : "text-muted-foreground hover:bg-muted",
                        )}
                        onClick={() => setNodeMode(node.key, mode)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  {node.input_mode === "form" ? (
                    <>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor={`offset-${node.key}`}>建议天数</Label>
                          <Input
                            id={`offset-${node.key}`}
                            type="number"
                            min={0}
                            step={1}
                            value={node.offset_days}
                            disabled={deleted}
                            onChange={(event) =>
                              updateNode(node.key, { offset_days: Number(event.target.value) || 0 })
                            }
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor={`channel-${node.key}`}>渠道偏好</Label>
                          <select
                            id={`channel-${node.key}`}
                            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs"
                            value={node.channel_preference || ""}
                            disabled={deleted || selectableChannels.length === 0}
                            onChange={(event) =>
                              updateNode(node.key, {
                                channel_preference: event.target.value as JourneyChannelPreference,
                              })
                            }
                          >
                            {selectableChannels.length === 0 ? (
                              <option value="">暂无已开启渠道</option>
                            ) : (
                              selectableChannels.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.label}
                                </option>
                              ))
                            )}
                          </select>
                          {!prefEnabled ? (
                            <p className="text-xs text-amber-700 dark:text-amber-400">
                              当前偏好未开启，请改选已开启渠道或前往
                              <Link to={`/accounts/${accountId}/channels`} className="underline">
                                渠道
                              </Link>
                            </p>
                          ) : null}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`trigger-${node.key}`}>一行触发</Label>
                        <Input
                          id={`trigger-${node.key}`}
                          value={node.trigger ?? ""}
                          disabled={deleted}
                          onChange={(event) => updateNode(node.key, { trigger: event.target.value })}
                          placeholder="例如：客户试用即将到期时考虑"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`instruction-${node.key}`}>执行说明</Label>
                        <Textarea
                          id={`instruction-${node.key}`}
                          rows={3}
                          value={node.instruction}
                          disabled={deleted}
                          onChange={(event) => updateNode(node.key, { instruction: event.target.value })}
                          placeholder="给旅程 AI 的步骤说明（非群发原文）"
                          maxLength={2000}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`window-start-${node.key}`}>建议发送时段</Label>
                        <div className="flex flex-wrap items-center gap-2">
                          <Input
                            id={`window-start-${node.key}`}
                            type="time"
                            className="w-[8.5rem]"
                            value={node.send_window_start}
                            disabled={deleted}
                            onChange={(event) =>
                              updateNode(node.key, { send_window_start: normalizeHhmm(event.target.value) })
                            }
                            required
                          />
                          <span className="text-sm text-muted-foreground" aria-hidden>
                            –
                          </span>
                          <Input
                            id={`window-end-${node.key}`}
                            type="time"
                            className="w-[8.5rem]"
                            value={node.send_window_end}
                            disabled={deleted}
                            onChange={(event) =>
                              updateNode(node.key, { send_window_end: normalizeHhmm(event.target.value) })
                            }
                            required
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="space-y-2">
                      <Label htmlFor={`prompt-${node.key}`}>节点提示词</Label>
                      <Textarea
                        id={`prompt-${node.key}`}
                        rows={6}
                        value={node.prompt ?? ""}
                        disabled={deleted}
                        onChange={(event) => updateNode(node.key, { prompt: event.target.value })}
                        placeholder="自由写时机、渠道与风格等，由旅程 AI 理解后规划"
                        maxLength={8000}
                      />
                    </div>
                  )}
                </div>
                <div className="flex justify-center">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs text-muted-foreground"
                    onClick={() => insertBelow(index)}
                  >
                    <Plus data-icon="inline-start" />
                    在此下方插入
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </form>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>删除整趟旅程？</DialogTitle>
            <DialogDescription>
              将永久删除「{name || "未命名旅程"}」及其全部节点，无法恢复。节点上的「删除」只删单步，这里删的是整趟旅程。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" disabled={isDeleting} onClick={() => setDeleteOpen(false)}>
              取消
            </Button>
            <Button type="button" variant="destructive" disabled={isDeleting} onClick={() => void handleDelete()}>
              {isDeleting ? "删除中…" : "确认删除"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
